import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { MenuTooltipTool } from '../../../../../../viewer/tools/tooltip-helper';
import { CoordinatePropertiesStyled } from '../../../tooltip/components/coordinate-properties';
import {
  AssetPositionProperties,
  hasTooltipContent,
  SkyviewAssetPositionProps,
} from '../../../tooltip/skyview-asset-position-tooltip';

type Props = {
  tool: MenuTooltipTool;
  light: boolean;
  /**
   * Shows the tooltip of the tool again, so that it moves between the cursor and the menu as soon
   * as it's placed there or removed. Otherwise it moves as the cursor is moved.
   */
  refresh?: () => void;
  /**
   * Styles the space it takes, e.g. without the margins it has in the right-side menu.
   */
  className?: string;
};

/**
 * The tooltip of a tool, pinned at the top of a menu or a dialog instead of following the cursor,
 * e.g. of the design tool in the right-side menu.
 *
 * It takes the same space in the menu whatever it shows, so that the sections below don't move,
 * and is shown over them when it needs more. That is only while the cursor is in the view, since
 * it is cleared when the cursor leaves it.
 */
export const MenuTooltip = (props: Props) => {
  const { t } = useTranslation();
  const [tooltip, setTooltip] = React.useState<SkyviewAssetPositionProps>();
  const refresh = React.useRef(props.refresh);
  refresh.current = props.refresh;

  React.useEffect(() => {
    props.tool.onMenuTooltipChanged = setTooltip;
    // Moves a tooltip shown next to the cursor into the menu, e.g. when the setting is turned on
    // with the keyboard while the cursor is in the view.
    refresh.current?.();

    return () => {
      props.tool.onMenuTooltipChanged = undefined;
      // And back next to the cursor. Nothing is shown when the tool is turned off, since the
      // cursor is then no longer in the view for it.
      refresh.current?.();
    };
  }, [props.tool]);

  return (
    <Slot className={props.className}>
      <Component data-testid="design-menu-tooltip" light={props.light}>
        {tooltip && hasTooltipContent(tooltip) ? (
          <AssetPositionProperties
            coordinate={tooltip.coordinate}
            coordinateSystem={tooltip.coordinateSystem}
            heights={tooltip.heights}
            properties={tooltip.properties}
          />
        ) : (
          <Placeholder>{t('menuTooltip.placeholder', { ns: 'skyviewDesign' })}</Placeholder>
        )}
      </Component>
    </Slot>
  );
};

const Slot = styled.div`
  position: relative;
  // Over the sections below.
  z-index: 1;
  flex-shrink: 0;

  height: 4em;
  margin: 0.8em 0.8em 0 0.8em;
  font-size: 10pt;
`;

const Placeholder = styled.span`
  color: #fffa;
`;

const Component = styled.div<{ light: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  min-height: 100%;
  box-sizing: border-box;

  padding: 0.5em;
  border-radius: 0.5em;

  // Opaque, unlike next to the cursor, since it is shown over the sections.
  background-color: rgb(64, 64, 64);
  color: #fff;
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.16),
    0 3px 6px rgba(0, 0, 0, 0.23);

  font-family: 'Montserrat', sans-serif;

  // Next to the cursor the tooltip is as wide as it needs, here it fits the menu or the dialog: the
  // columns share the width, the labels wrap and nothing has a minimum width. Values don't wrap.
  && * {
    min-width: 0;
  }

  && ${CoordinatePropertiesStyled} {
    grid-auto-columns: minmax(0, 1fr);
    grid-column-gap: 1em;
    grid-row-gap: 0.5em;

    label {
      white-space: normal;
    }
  }

  // The content of the tooltip is made for a dark background.
  ${(props) =>
    props.light &&
    css`
      border: 1px solid ${props.theme.color.gray.light};
      background-color: #fff;
      color: ${props.theme.color.gray.darkest};

      && label,
      && ${Placeholder} {
        color: ${props.theme.color.gray.dark};
      }

      && input {
        background-color: ${props.theme.color.gray.lightest};
      }
    `}
`;
